import {
  faBug,
  faCheck,
  faChevronDown,
  faChevronUp,
  faCode,
  faCodeBranch,
  faExternalLinkAlt,
  faFileAlt,
  faGear,
  faSpinner,
  faStar,
  faTimes,
  faWrench,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Breadcrumb } from "../components/ui";
import { UserAuth } from "../context/AuthContext";

const TypeBadge = ({ type }) => (
  <span className="text-[10px] px-2 py-0.5 rounded-full bg-base-content/5 text-base-content/40">
    {type}
  </span>
);

// Health check card - generic for Backend, Database, Proxy
const HealthCard = ({ name, type, status, loading, details, link, linkLabel }) => {
  const isWorking = status?.status === "ok" || status?.working === true;
  const isError = status?.status === "error";

  return (
    <div className="bg-base-100 rounded-lg p-4 border border-base-content/10">
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="font-medium text-sm text-base-content">{name}</span>
        <TypeBadge type={type} />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[10px] text-base-content/40">
          <FontAwesomeIcon icon={faSpinner} spin />
          <span>Checking...</span>
        </div>
      ) : isWorking ? (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            <FontAwesomeIcon icon={faCheck} className="text-success text-[10px]" />
            <span className="text-[10px] text-base-content/50">Connected</span>
            {status?.latency_ms && (
              <span className="text-[10px] text-base-content/30">{status.latency_ms}ms</span>
            )}
          </div>
          {details}
        </div>
      ) : isError ? (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            <FontAwesomeIcon icon={faTimes} className="text-base-content/40 text-[10px]" />
            <span className="text-[10px] text-base-content/50">
              {status?.error === "NO_CREDITS" ? "No credits" : "Failed"}
            </span>
          </div>
          {status?.message && (
            <p className="text-[10px] text-base-content/40">{status.message}</p>
          )}
        </div>
      ) : (
        <div className="text-[10px] text-base-content/40">Not configured</div>
      )}

      {link && (
        <div className="mt-3 pt-2 border-t border-base-content/5">
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] text-base-content/40 hover:text-base-content/60 flex items-center gap-1"
          >
            {linkLabel || "Dashboard"}
            <FontAwesomeIcon icon={faExternalLinkAlt} className="text-[8px]" />
          </a>
        </div>
      )}
    </div>
  );
};

// Core Infrastructure section
const CoreInfraSection = ({ healthStatus, loading, host }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
    <HealthCard
      name="EC2 Server"
      type="Backend"
      status={healthStatus.backend}
      loading={loading.backend}
      details={
        <div className="text-[10px] text-base-content/40 space-y-0.5">
          <p className="font-mono truncate">{host?.replace(/^https?:\/\//, '')}</p>
          <p className="font-mono">us-east-1</p>
        </div>
      }
      link="https://console.aws.amazon.com/ec2"
      linkLabel="AWS Console"
    />
    <HealthCard
      name="monika.services"
      type="Domain"
      status={{ status: "ok" }}
      loading={false}
      details={
        <p className="text-[10px] text-base-content/40 font-mono truncate">main.dpofdmsxiooa6.amplifyapp.com</p>
      }
      link="https://us-east-1.console.aws.amazon.com/amplify"
      linkLabel="Amplify"
    />
    <HealthCard
      name="PostgreSQL (RDS)"
      type="Database"
      status={healthStatus.database}
      loading={loading.database}
      details={
        <div className="text-[10px] text-base-content/40 space-y-0.5">
          <p className="font-mono truncate">monika.cfgidcmtqw13.us-east-1.rds.amazonaws.com</p>
          {healthStatus.database?.stats && (
            <div className="flex gap-3 mt-1">
              <span>{healthStatus.database.stats.projects} projects</span>
              <span>{healthStatus.database.stats.names} names</span>
            </div>
          )}
        </div>
      }
      link="https://console.aws.amazon.com/rds"
      linkLabel="RDS Console"
    />
    <HealthCard
      name="Firebase Auth"
      type="Auth"
      status={{ status: "ok" }}
      loading={false}
      details={
        <p className="text-[10px] text-base-content/40">User authentication</p>
      }
      link="https://console.firebase.google.com"
      linkLabel="Firebase Console"
    />
    <HealthCard
      name="Firebase Firestore"
      type="Database"
      status={{ status: "ok" }}
      loading={false}
      details={
        <p className="text-[10px] text-base-content/40">User preferences & sessions</p>
      }
      link="https://console.firebase.google.com"
      linkLabel="Firebase Console"
    />
  </div>
);

// Auxiliary Services section
const AuxiliarySection = ({ healthStatus, loading }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
    <HealthCard
      name="Azure OpenAI"
      type="LLM"
      status={{ status: "ok" }}
      loading={false}
      details={
        <p className="text-[10px] text-base-content/40">GPT-4 for generation & filtering</p>
      }
      link="https://portal.azure.com"
      linkLabel="Azure Portal"
    />
    <HealthCard
      name="Google Cloud"
      type="API"
      status={{ status: "ok" }}
      loading={false}
      details={
        <p className="text-[10px] text-base-content/40">Sheets export, Gmail notifications</p>
      }
      link="https://console.cloud.google.com"
      linkLabel="Google Console"
    />
    <HealthCard
      name="Oxylabs"
      type="Proxy"
      status={healthStatus.proxy}
      loading={loading.proxy}
      details={
        healthStatus.proxy?.usage && (
          <div className="text-[10px] text-base-content/40 space-y-0.5">
            <div className="flex justify-between">
              <span>This month</span>
              <span>{healthStatus.proxy.usage.this_month_gb} GB</span>
            </div>
            <div className="flex justify-between">
              <span>Lifetime</span>
              <span>{healthStatus.proxy.usage.lifetime_gb} GB</span>
            </div>
          </div>
        )
      }
      link="https://dashboard.oxylabs.io/en/overview/RP"
      linkLabel={healthStatus.proxy?.error === "NO_CREDITS" ? "Buy Credits" : "Dashboard"}
    />
  </div>
);

// Commit type icon
const CommitTypeIcon = ({ type }) => {
  const cls = "text-[10px]";
  switch (type) {
    case "feat":
      return <FontAwesomeIcon icon={faStar} className={`${cls} text-success`} />;
    case "fix":
      return <FontAwesomeIcon icon={faBug} className={`${cls} text-error`} />;
    case "refactor":
      return <FontAwesomeIcon icon={faWrench} className={`${cls} text-info`} />;
    case "docs":
      return <FontAwesomeIcon icon={faFileAlt} className={`${cls} text-primary`} />;
    case "chore":
      return <FontAwesomeIcon icon={faGear} className={`${cls} text-base-content/40`} />;
    case "perf":
      return <FontAwesomeIcon icon={faCodeBranch} className={`${cls} text-warning`} />;
    default:
      return <FontAwesomeIcon icon={faCodeBranch} className={`${cls} text-base-content/40`} />;
  }
};

// Single commit row
const CommitRow = ({ commit }) => {
  const type = commit.type || "other";

  return (
    <div className="flex items-start gap-2.5 py-1.5">
      {commit.avatarUrl ? (
        <img
          src={commit.avatarUrl}
          alt={commit.author}
          className="w-8 h-8 rounded-full flex-shrink-0"
        />
      ) : (
        <div className="w-8 h-8 rounded-full bg-base-content/10 flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <CommitTypeIcon type={type} />
          <span className="text-[10px] text-base-content/40 font-mono">{commit.shortSha || commit.sha?.slice(0, 7)}</span>
        </div>
        <span className="block text-[11px] text-base-content truncate">{commit.message}</span>
        <span className="block text-[9px] text-base-content/30">
          {commit.authorUsername || commit.author} · {commit.date}
        </span>
      </div>
    </div>
  );
};

// Commits section
const CommitsSection = ({ commits, loading, expanded, onToggle }) => {
  const totalCommits = (commits.frontend?.length || 0) + (commits.backend?.length || 0);

  return (
    <div className="bg-base-100 rounded-lg border border-base-content/10">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-base-content/5 transition-colors"
      >
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faCodeBranch} className="text-base-content/40 text-sm" />
          <span className="text-sm font-medium text-base-content">Recent Commits</span>
        </div>
        <div className="flex items-center gap-2">
          {totalCommits > 0 && (
            <span className="text-[10px] text-base-content/40">{totalCommits} commits</span>
          )}
          <FontAwesomeIcon
            icon={expanded ? faChevronUp : faChevronDown}
            className="text-base-content/30 text-xs"
          />
        </div>
      </button>

      {expanded && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 px-4 pb-4">
          {loading ? (
            <div className="col-span-2 flex items-center gap-2 text-[10px] text-base-content/40 py-2">
              <FontAwesomeIcon icon={faSpinner} spin />
              <span>Loading...</span>
            </div>
          ) : (
            <>
              {/* Frontend Commits */}
              <div>
                <div className="flex items-center justify-between mb-2 pb-1 border-b border-base-content/5">
                  <span className="text-[10px] font-medium text-base-content/50 uppercase tracking-wider">Frontend</span>
                  <span className="text-[10px] text-base-content/30">main</span>
                </div>
                {commits.frontend?.length > 0 ? (
                  <div className="space-y-1">
                    {commits.frontend.slice(0, 10).map((commit) => (
                      <CommitRow key={commit.sha} commit={commit} />
                    ))}
                  </div>
                ) : (
                  <p className="text-[10px] text-base-content/40">No commits</p>
                )}
              </div>

              {/* Backend Commits */}
              <div>
                <div className="flex items-center justify-between mb-2 pb-1 border-b border-base-content/5">
                  <span className="text-[10px] font-medium text-base-content/50 uppercase tracking-wider">Backend</span>
                  <span className="text-[10px] text-base-content/30">main</span>
                </div>
                {commits.backend?.length > 0 ? (
                  <div className="space-y-1">
                    {commits.backend.slice(0, 10).map((commit) => (
                      <CommitRow key={commit.sha} commit={commit} />
                    ))}
                  </div>
                ) : (
                  <p className="text-[10px] text-base-content/40">No commits</p>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

function AdminInfrastructure() {
  const { user } = UserAuth();
  const navigate = useNavigate();

  const [healthStatus, setHealthStatus] = useState({
    backend: null,
    database: null,
    proxy: null,
  });
  const [loading, setLoading] = useState({
    backend: true,
    database: true,
    proxy: true,
  });
  const [commits, setCommits] = useState({ frontend: [], backend: [] });
  const [commitsLoading, setCommitsLoading] = useState(false);
  const [commitsExpanded, setCommitsExpanded] = useState(false);

  const host = process.env.REACT_APP_SERVER;

  useEffect(() => {
    if (user === null) {
      navigate("/signin");
    }
  }, [user, navigate]);

  // Fetch all health statuses
  useEffect(() => {
    if (!user?.email) return;

    // Backend health check
    const fetchBackend = async () => {
      try {
        const start = Date.now();
        const response = await fetch(`${host}/api/admin/health`);
        const data = await response.json();
        setHealthStatus(prev => ({
          ...prev,
          backend: { ...data, latency_ms: Date.now() - start, tested_at: new Date().toISOString() }
        }));
      } catch (error) {
        setHealthStatus(prev => ({
          ...prev,
          backend: { status: "error", message: "Failed to connect", tested_at: new Date().toISOString() }
        }));
      } finally {
        setLoading(prev => ({ ...prev, backend: false }));
      }
    };

    // Database health check
    const fetchDatabase = async () => {
      try {
        const start = Date.now();
        const response = await fetch(`${host}/api/admin/db-status`);
        const data = await response.json();
        setHealthStatus(prev => ({
          ...prev,
          database: { ...data, latency_ms: Date.now() - start, tested_at: new Date().toISOString() }
        }));
      } catch (error) {
        setHealthStatus(prev => ({
          ...prev,
          database: { status: "error", message: "Failed to connect", tested_at: new Date().toISOString() }
        }));
      } finally {
        setLoading(prev => ({ ...prev, database: false }));
      }
    };

    // Proxy health check
    const fetchProxy = async () => {
      try {
        const response = await fetch(`${host}/api/admin/proxy-status`);
        const data = await response.json();
        setHealthStatus(prev => ({ ...prev, proxy: data }));
      } catch (error) {
        setHealthStatus(prev => ({
          ...prev,
          proxy: { status: "error", message: "Failed to connect", tested_at: new Date().toISOString() }
        }));
      } finally {
        setLoading(prev => ({ ...prev, proxy: false }));
      }
    };

    fetchBackend();
    fetchDatabase();
    fetchProxy();
  }, [host, user?.email]);

  // Fetch commits when expanded
  useEffect(() => {
    if (!commitsExpanded || commits.frontend.length > 0 || commits.backend.length > 0) return;

    const fetchCommits = async () => {
      setCommitsLoading(true);
      try {
        const response = await fetch(`${host}/api/admin/commits`);
        const data = await response.json();
        setCommits(data);
      } catch (error) {
        console.error("Failed to fetch commits:", error);
      } finally {
        setCommitsLoading(false);
      }
    };

    fetchCommits();
  }, [commitsExpanded, host, commits.frontend.length, commits.backend.length]);

  if (!user?.email) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Admin", href: "/admin" },
          { label: "Infrastructure" },
        ]}
        className="mb-4"
      />

      {/* Core Infrastructure */}
      <div className="mb-8">
        <h2 className="text-sm font-medium text-base-content/60 uppercase tracking-wider mb-3">Core</h2>
        <CoreInfraSection healthStatus={healthStatus} loading={loading} host={host} />
      </div>

      {/* Auxiliary Services */}
      <div className="mb-8">
        <h2 className="text-sm font-medium text-base-content/60 uppercase tracking-wider mb-3">Auxiliary</h2>
        <AuxiliarySection healthStatus={healthStatus} loading={loading} />
      </div>

      {/* Recent Commits */}
      <CommitsSection
        commits={commits}
        loading={commitsLoading}
        expanded={commitsExpanded}
        onToggle={() => setCommitsExpanded(!commitsExpanded)}
      />
    </div>
  );
}

export default AdminInfrastructure;
